import { Link } from "react-router-dom";
import { ArrowDown, ArrowUpRight, Star, ChevronRight, Quote, MapPin, Compass, Sparkles, Clock, Phone } from "lucide-react";
import { useEffect, useState } from "react";
import Reveal from "../components/Reveal";
import QuoteForm from "../components/QuoteForm";
import Seo from "../components/Seo";
import { publicApi } from "../lib/api";
import { experiences } from "../data/experiences";

export default function Home() {
  const [testimonials, setTestimonials] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [inbound, setInbound] = useState([]);
  const [outbound, setOutbound] = useState([]);

  useEffect(() => {
    publicApi.get("/testimonials").then((r) => setTestimonials(r.data)).catch(() => {});
    publicApi.get("/blog").then((r) => setBlogPosts(r.data.slice(0, 3))).catch(() => {});
    publicApi.get("/destinations").then((r) => {
      const all = r.data;
      setInbound(all.filter((d) => d.category === "inbound").slice(0, 5));
      setOutbound(all.filter((d) => d.category === "outbound").slice(0, 8));
    }).catch(() => {});
  }, []);

  return (
    <div className="overflow-x-hidden">
      <Seo
        title="Voyage Luxe — Luxury Journeys Across India & Beyond"
        description="A private destination management atelier curating bespoke luxury journeys across India and the world — Rajasthan, Maldives, Kerala, Japan, Switzerland and more. 24-hour quote, concierge service, palace hotels."
        image="https://images.unsplash.com/photo-1721572321878-46fbef2e4bbd?w=1600&q=80"
        schema={{
          "@context": "https://schema.org",
          "@type": "TravelAgency",
          name: "Voyage Luxe",
          description: "Luxury travel & destination management company curating private inbound India tours and outbound international journeys.",
          url: typeof window !== "undefined" ? window.location.origin : "",
          telephone: "+91-9555647288",
          email: "concierge@voyageluxe.com",
          address: { "@type": "PostalAddress", streetAddress: "11 Aurangzeb Road", addressLocality: "New Delhi", postalCode: "110011", addressCountry: "IN" },
          areaServed: ["India", "Maldives", "Dubai", "Switzerland", "Japan", "Europe"],
          priceRange: "$$$$",
        }}
      />
      {/* HERO */}
      <section className="relative h-screen min-h-[720px] w-full overflow-hidden" data-testid="hero-section">
        <img
          src="https://images.unsplash.com/photo-1721572321878-46fbef2e4bbd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzF8MHwxfHNlYXJjaHwxfHxSYWphc3RoYW4lMjBsdXh1cnklMjBwYWxhY2UlMjBob3RlbCUyMGFyY2hpdGVjdHVyZXxlbnwwfHx8fDE3Nzk1MTk2ODV8MA&ixlib=rb-4.1.0&q=85"
          alt="Rajasthan Palace"
          className="absolute inset-0 h-full w-full object-cover animate-ken-burns"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/15 to-black/55" />
        <div className="absolute inset-0 grain" />

        <div className="relative z-10 h-full container-luxe flex flex-col">
          <div className="flex-1 flex flex-col justify-end pb-24 md:pb-32 text-white">
            <Reveal>
              <div className="eyebrow text-white/70 mb-6">Voyage Luxe · Est. MMXVII</div>
            </Reveal>
            <Reveal delay={150}>
              <h1 className="font-serif font-light text-[clamp(2.75rem,7vw,6.5rem)] leading-[0.95] tracking-[-0.02em] text-balance max-w-5xl">
                Luxury Journeys<br />
                <span className="italic font-light">Across India</span> <span className="opacity-70">&amp;</span> Beyond
              </h1>
            </Reveal>
            <Reveal delay={300}>
              <p className="mt-8 text-white/80 max-w-xl text-base md:text-lg leading-relaxed">
                A private travel atelier curating bespoke journeys — palaces, paradises, and the quiet places between — for guests for whom ordinary will not do.
              </p>
            </Reveal>
            <Reveal delay={450}>
              <div className="mt-10 flex flex-col sm:flex-row items-start gap-4">
                <Link
                  to="/destinations?region=India"
                  data-testid="hero-cta-explore-india"
                  className="group inline-flex items-center gap-3 px-9 py-4 bg-white text-charcoal hover:bg-accent hover:text-accent-foreground transition-colors duration-500 uppercase text-[11px] tracking-[0.3em]"
                >
                  Explore India
                  <ArrowUpRight size={14} strokeWidth={1.5} className="group-hover:rotate-45 transition-transform duration-500" />
                </Link>
                <Link
                  to="/contact"
                  data-testid="hero-cta-plan-journey"
                  className="group inline-flex items-center gap-3 px-9 py-4 border border-white/60 text-white hover:bg-white hover:text-charcoal transition-colors duration-500 uppercase text-[11px] tracking-[0.3em]"
                >
                  Plan Your Journey
                  <ArrowUpRight size={14} strokeWidth={1.5} className="group-hover:rotate-45 transition-transform duration-500" />
                </Link>
              </div>
            </Reveal>
          </div>

          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/70 flex flex-col items-center gap-2 text-[10px] uppercase tracking-[0.4em]">
            <span>Scroll</span>
            <ArrowDown size={14} strokeWidth={1} className="animate-bounce" />
          </div>
        </div>
      </section>

      {/* DUAL EXPERIENCE */}
      <section className="py-24 md:py-36 bg-background" data-testid="dual-experience">
        <div className="container-luxe">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16 md:mb-24">
              <div>
                <div className="eyebrow mb-4">Two Atlases · One Atelier</div>
                <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-2xl text-balance">
                  Inbound to India.<br />
                  <span className="italic text-accent">Outbound</span> to the world.
                </h2>
              </div>
              <p className="text-foreground/70 max-w-md leading-relaxed">
                For nearly a decade we have crafted private journeys for international guests entering India, and Indian connoisseurs travelling abroad. Two languages, one standard of care.
              </p>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 md:gap-8">
            {[
              {
                title: "Inbound · Luxury India",
                tagline: "Palaces, deserts, backwaters, and the Himalaya.",
                img: "https://images.unsplash.com/photo-1599661046289-e31897846e41?w=1600&q=80&auto=format&fit=crop",
                to: "/destinations?region=India",
                stat: "10 destinations · 6–14 nights",
              },
              {
                title: "Outbound · International",
                tagline: "Maldives sandbanks, Swiss heights, Kyoto stillness.",
                img: "https://images.pexels.com/photos/9394274/pexels-photo-9394274.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1600&w=2400",
                to: "/destinations?region=International",
                stat: "8 destinations · 5–14 nights",
              },
            ].map((c, i) => (
              <Reveal key={c.title} delay={i * 150}>
                <Link
                  to={c.to}
                  data-testid={`dual-card-${i === 0 ? 'inbound' : 'outbound'}`}
                  className="group relative block aspect-[4/5] md:aspect-[3/4] overflow-hidden"
                >
                  <img src={c.img} alt={c.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
                  <div className="relative z-10 h-full flex flex-col justify-end p-8 md:p-12 text-white">
                    <div className="eyebrow text-white/60 mb-3">{c.stat}</div>
                    <h3 className="font-serif text-3xl md:text-5xl leading-tight tracking-tight">{c.title}</h3>
                    <p className="mt-3 text-white/80 max-w-sm">{c.tagline}</p>
                    <div className="mt-8 inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-accent">
                      Discover <ChevronRight size={14} className="group-hover:translate-x-1 transition-transform" />
                    </div>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED DESTINATIONS */}
      <section className="py-24 md:py-36 bg-secondary/40" data-testid="featured-destinations">
        <div className="container-luxe">
          <Reveal>
            <div className="text-center mb-16 md:mb-20">
              <div className="eyebrow mb-4">Featured Destinations</div>
              <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-balance max-w-3xl mx-auto">
                Ten chapters of <span className="italic">unforgettable</span>.
              </h2>
            </div>
          </Reveal>

          {/* India sub-grid */}
          <div className="mb-16">
            <Reveal>
              <div className="flex items-end justify-between mb-6">
                <h3 className="font-serif text-2xl md:text-3xl">India</h3>
                <Link to="/destinations" className="eyebrow hover:text-accent transition-colors">View all →</Link>
              </div>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {inbound.map((d, i) => (
                <Reveal key={d.slug} delay={i * 80}>
                  <DestCard d={d} />
                </Reveal>
              ))}
            </div>
          </div>

          {/* International sub-grid */}
          <div>
            <Reveal>
              <h3 className="font-serif text-2xl md:text-3xl mb-6">Beyond</h3>
            </Reveal>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-6">
              {outbound.map((d, i) => (
                <Reveal key={d.slug} delay={i * 80}>
                  <DestCard d={d} />
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* "GET QUOTE IN 24 HOURS" BANNER */}
      <section className="relative overflow-hidden" data-testid="quote24-banner">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1582719508461-905c673771fd?w=2400&q=80&auto=format&fit=crop"
            alt=""
            className="h-full w-full object-cover"
          />
          <div className="absolute inset-0 bg-black/65" />
        </div>
        <div className="container-luxe relative py-20 md:py-28">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 text-white">
            <Reveal>
              <div className="eyebrow text-white/60 mb-4">No Templates · No Hold Music</div>
              <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight max-w-2xl text-balance">
                A private quote in your inbox within<br />
                <span className="italic text-accent">24 hours</span>.
              </h2>
            </Reveal>
            <Reveal delay={200}>
              <Link
                to="/contact"
                data-testid="quote24-cta"
                className="group inline-flex items-center gap-3 px-10 py-5 bg-white text-charcoal hover:bg-accent hover:text-accent-foreground transition-colors duration-500 uppercase text-[11px] tracking-[0.3em]"
              >
                Request Your Quote
                <ArrowUpRight size={14} strokeWidth={1.5} className="group-hover:rotate-45 transition-transform duration-500" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* SIGNATURE EXPERIENCES */}
      <section className="py-24 md:py-36 bg-background" data-testid="signature-experiences">
        <div className="container-luxe">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-16">
              <div>
                <div className="eyebrow mb-4">Signature Experiences</div>
                <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-balance max-w-2xl">
                  Moments arranged with <span className="italic">quiet precision</span>.
                </h2>
              </div>
              <Link to="/experiences" data-testid="experiences-view-all" className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.3em] text-foreground/70 hover:text-accent transition-colors">
                Explore all experiences <ArrowUpRight size={14} />
              </Link>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {experiences.map((e, i) => (
              <Reveal key={e.id} delay={i * 100}>
                <div className="group relative aspect-[4/5] overflow-hidden">
                  <img src={e.image} alt={e.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-transparent" />
                  <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                    <div className="eyebrow text-accent mb-2">{e.eyebrow}</div>
                    <h3 className="font-serif text-3xl tracking-tight">{e.title}</h3>
                    <p className="mt-2 text-sm text-white/75 max-w-xs">{e.short}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="py-24 md:py-36 bg-secondary/30 relative" data-testid="why-choose-us">
        <div className="container-luxe">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20 items-start">
            <Reveal className="lg:col-span-5 lg:sticky lg:top-32">
              <div className="eyebrow mb-4">Why Voyage Luxe</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-[1.05] tracking-tight text-balance">
                A standard of care that <span className="italic">does not exist elsewhere</span>.
              </h2>
              <p className="mt-6 text-foreground/70 leading-relaxed max-w-md">
                We are a small atelier by choice. Fewer guests means deeper relationships — with you, and with the palace owners, chefs, and guides who become part of your journey.
              </p>
            </Reveal>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-px bg-border">
              {[
                { icon: Sparkles, t: "Bespoke Itineraries", d: "Every journey designed from a blank page around your particular curiosity, season, and pace." },
                { icon: Clock, t: "24/7 Concierge", d: "A single dedicated concierge from first call until you return home — and well beyond." },
                { icon: Compass, t: "Private Access", d: "After-hours monuments, closed palaces, sold-out tables — opened for our guests alone." },
                { icon: MapPin, t: "Local Atelier", d: "On-the-ground teams in every destination, not remote agents pretending otherwise." },
                { icon: Star, t: "Hospitality Partners", d: "Preferred partner status with Aman, Oberoi, Taj, Soneva, Four Seasons & more." },
                { icon: Phone, t: "Trusted Expertise", d: "Eight years, 2,400 guests, 64 countries. Discreet references on request." },
              ].map((item, i) => (
                <Reveal key={item.t} delay={i * 80} className="bg-background p-8 md:p-10">
                  <item.icon size={22} strokeWidth={1.25} className="text-accent" />
                  <h4 className="mt-5 font-serif text-2xl">{item.t}</h4>
                  <p className="mt-3 text-sm text-foreground/65 leading-relaxed">{item.d}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="py-24 md:py-36 bg-background" data-testid="testimonials-section">
        <div className="container-luxe">
          <Reveal>
            <div className="text-center max-w-2xl mx-auto mb-16">
              <div className="eyebrow mb-4">In Their Words</div>
              <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight">
                Letters from <span className="italic">our guests</span>.
              </h2>
            </div>
          </Reveal>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {testimonials.slice(0, 6).map((t, i) => (
              <Reveal key={t.id} delay={i * 100}>
                <article className="h-full bg-secondary/40 border border-border p-8 md:p-10 flex flex-col">
                  <Quote size={28} strokeWidth={1} className="text-accent mb-6" />
                  <div className="flex items-center gap-1 mb-4" aria-label={`${t.rating} stars`}>
                    {Array.from({ length: t.rating }).map((_, k) => (
                      <Star key={k} size={13} fill="currentColor" strokeWidth={0} className="text-accent" />
                    ))}
                  </div>
                  <p className="font-serif text-xl md:text-2xl leading-snug text-foreground/90 flex-1">
                    “{t.quote}”
                  </p>
                  <div className="mt-8 flex items-center gap-4 pt-6 border-t border-border">
                    <img src={t.avatar} alt={t.name} className="h-12 w-12 rounded-full object-cover" />
                    <div>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-[11px] uppercase tracking-[0.2em] text-foreground/50 mt-0.5">{t.journey} · {t.location}</div>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE FORM */}
      <section className="py-24 md:py-36 relative overflow-hidden" data-testid="quote-section">
        <img
          src="https://images.unsplash.com/photo-1601907522726-16611718ce6a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzl8MHwxfHNlYXJjaHwzfHxIaW1hbGF5YXMlMjBsdXh1cnklMjByZXRyZWF0JTIwc25vd3xlbnwwfHx8fDE3Nzk1MTk3MTJ8MA&ixlib=rb-4.1.0&q=85"
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-15"
        />
        <div className="absolute inset-0 bg-background/85" />
        <div className="container-luxe relative">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16">
            <Reveal className="lg:col-span-5">
              <div className="eyebrow mb-4">Begin Your Journey</div>
              <h2 className="font-serif text-4xl md:text-6xl leading-[1.05] tracking-tight text-balance">
                Receive a curated quote within <span className="italic text-accent">24 hours</span>.
              </h2>
              <p className="mt-6 text-foreground/70 leading-relaxed max-w-md">
                Tell us where you dream of going and how you wish to feel. One of our concierges will compose a private proposal — never templated, never rushed.
              </p>
              <div className="mt-12 hairline pt-8">
                <div className="eyebrow mb-2">Prefer to Speak?</div>
                <a href="tel:+919555647288" className="font-serif text-2xl hover:text-accent transition-colors">+91 95556 47288</a>
              </div>
            </Reveal>
            <Reveal className="lg:col-span-7" delay={200}>
              <QuoteForm />
            </Reveal>
          </div>
        </div>
      </section>

      {/* JOURNAL PREVIEW */}
      <section className="py-24 md:py-36 bg-secondary/30" data-testid="journal-preview">
        <div className="container-luxe">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
              <div>
                <div className="eyebrow mb-4">The Journal</div>
                <h2 className="font-serif text-4xl md:text-5xl leading-tight tracking-tight max-w-xl">Field notes from <span className="italic">our atelier</span>.</h2>
              </div>
              <Link to="/journal" className="eyebrow hover:text-accent transition-colors">Read all entries →</Link>
            </div>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
            {blogPosts.slice(0, 3).map((p, i) => (
              <Reveal key={p.id || p.slug} delay={i * 100}>
                <Link to={`/journal/${p.slug}`} className="group block" data-testid={`home-blog-${p.slug}`}>
                  <div className="aspect-[4/5] overflow-hidden">
                    <img src={p.image} alt={p.title} className="h-full w-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-105" />
                  </div>
                  <div className="mt-5 eyebrow text-foreground/50">{p.category} · {p.read_time}</div>
                  <h3 className="mt-2 font-serif text-2xl md:text-3xl leading-tight group-hover:text-accent transition-colors">{p.title}</h3>
                  <p className="mt-3 text-sm text-foreground/65 leading-relaxed">{p.excerpt}</p>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

function DestCard({ d }) {
  return (
    <Link
      to={`/destinations/${d.slug}`}
      data-testid={`destination-card-${d.slug}`}
      className="group block"
    >
      <div className="aspect-[3/4] overflow-hidden bg-secondary">
        <img src={d.hero} alt={d.name} className="h-full w-full object-cover transition-transform duration-[1500ms] ease-out group-hover:scale-110" />
      </div>
      <div className="mt-4">
        <div className="font-serif text-xl md:text-2xl leading-tight group-hover:text-accent transition-colors">{d.name}</div>
        <div className="mt-1 text-[11px] uppercase tracking-[0.2em] text-foreground/50">From {d.price_from} · {d.nights} nts</div>
      </div>
    </Link>
  );
}
